 
/* eslint-disable @typescript-eslint/no-unused-vars */
 
 
 
 
 
import { useState, useMemo, memo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { useShallow } from 'zustand/react/shallow';
import type { Product } from '@smartphone-tycoon/shared-core';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { useInventoryStore } from '../../stores/inventoryStore';
import { useChartColors } from '../../hooks/useChartColors';
import { useSupplyDemandHistoryStore, getDisplayDaysForProduct } from '../../stores/supplyDemandHistoryStore';
import { useGameStore } from '../../stores/gameStore';
import { SupplyDemandChart } from './SupplyDemandChart';

ChartJS.register(ArcElement, Tooltip, Legend);

interface MarketInsightsProps {
  products: Product[];
}

interface ExpandedProduct {
  [productId: string]: boolean;
}

export const MarketInsights = memo(function MarketInsights({ products }: MarketInsightsProps) {
  const [expandedProducts, setExpandedProducts] = useState<ExpandedProduct>({});

  // Get inventory data
  const devices = useInventoryStore(state => state.devices);

  // Get theme-aware chart colors
  const chartColors = useChartColors();

  // Get current game date for adaptive chart display
  // Using useShallow to prevent creating new object references on every render
  const currentDate = useGameStore(useShallow(state => ({
    day: state.gameDate.day,
    month: state.gameDate.month,
    year: state.gameDate.year
  })));

  // Get supply/demand history
  const getProductHistory = useSupplyDemandHistoryStore(state => state.getProductHistory);

  const toggleProduct = (productId: string) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Filter to show only products in production, released, or discontinued
  const relevantProducts = useMemo(() =>
    products.filter(p =>
      p.status === 'production' || p.status === 'released' || p.status === 'discontinued'
    ), [products]
  );

  // Get market segments with theme-aware colors
  const segmentColors: { [key: string]: string } = useMemo(() => ({
    'Budget Conscious': chartColors[0],
    'Value Seekers': chartColors[1],
    'Tech Enthusiasts': chartColors[2],
    'Premium Users': chartColors[3],
    'Enterprise Professional': chartColors[4],
    'Senior Users': chartColors[5]
  }), [chartColors]);

  // Memoized function to distribute sales by segment based on product characteristics
  const getSegmentSales = useMemo(() => (product: Product) => {
    const totalSales = product.salesMetrics?.unitsSold || 0;
    if (totalSales === 0) return {};

    // Simple distribution based on price
    const price = product.price || 0;
    let distribution: { [key: string]: number } = {};

    if (price < 300) {
      distribution = {
        'Budget Conscious': 0.4,
        'Value Seekers': 0.3,
        'Senior Users': 0.15,
        'Tech Enthusiasts': 0.1,
        'Premium Users': 0.03,
        'Enterprise Professional': 0.02
      };
    } else if (price < 600) {
      distribution = {
        'Value Seekers': 0.35,
        'Budget Conscious': 0.2,
        'Tech Enthusiasts': 0.2,
        'Senior Users': 0.1,
        'Premium Users': 0.1,
        'Enterprise Professional': 0.05
      };
    } else if (price < 900) {
      distribution = {
        'Tech Enthusiasts': 0.3,
        'Premium Users': 0.25,
        'Value Seekers': 0.2,
        'Enterprise Professional': 0.15,
        'Budget Conscious': 0.05,
        'Senior Users': 0.05
      };
    } else {
      distribution = {
        'Premium Users': 0.35,
        'Tech Enthusiasts': 0.25,
        'Enterprise Professional': 0.2,
        'Value Seekers': 0.1,
        'Budget Conscious': 0.05,
        'Senior Users': 0.05
      };
    }

    const segmentSales: { [key: string]: number } = {};
    Object.entries(distribution).forEach(([segment, percentage]) => {
      segmentSales[segment] = Math.round(totalSales * percentage);
    });

    return segmentSales;
  }, []);

  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          boxWidth: 12,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: { label: string; parsed: number }) => {
            return `${context.label}: ${context.parsed.toLocaleString()} units`;
          }
        }
      }
    }
  }), []);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-theme-text-primary mb-4">Market Insights</h2>
      
      {relevantProducts.length === 0 ? (
        <div className="bg-theme-bg-content rounded-lg shadow p-8 text-center text-theme-text-muted">
          No products in production or discontinued yet
        </div>
      ) : (
        relevantProducts.map(product => {
          const isExpanded = expandedProducts[product.id];
          const segmentSales = getSegmentSales(product);
          
          // Use real reviews from product data, or empty arrays if not available
          const reviews = product.reviews ? [
            ...product.reviews.professional,
            ...product.reviews.customer
          ] : [];
          
          const pieData = {
            labels: Object.keys(segmentSales),
            datasets: [{
              data: Object.values(segmentSales),
              backgroundColor: Object.keys(segmentSales).map(segment => 
                segmentColors[segment as keyof typeof segmentColors]
              ),
              borderWidth: 1
            }]
          };

          return (
            <div key={product.id} className="bg-theme-bg-content rounded-lg shadow">
              {/* Collapsed View */}
              <button
                onClick={() => toggleProduct(product.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-theme-bg-secondary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-theme-text-muted text-lg">
                    {isExpanded ? '▼' : '▶'}
                  </span>
                  <h3 className="font-semibold text-lg text-theme-text-primary">{product.name}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    product.status === 'production'
                      ? 'bg-theme-success/20 text-theme-success'
                      : 'bg-theme-bg-tertiary text-theme-text-secondary'
                  }`}>
                    {product.status}
                  </span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <span className="text-theme-text-secondary">
                    Inventory: <span className="font-medium text-theme-text-primary">
                      {(devices.get(product.id)?.currentStock || 0).toLocaleString()}
                    </span>
                  </span>
                  <span className="text-theme-text-secondary">
                    Units Sold: <span className="font-medium text-theme-text-primary">
                      {(product.salesMetrics?.unitsSold || 0).toLocaleString()}
                    </span>
                  </span>
                  <span className="text-theme-text-secondary">
                    Revenue: <span className="font-medium text-theme-text-primary">
                      {formatCurrency(product.salesMetrics?.revenue || 0)}
                    </span>
                  </span>
                </div>
              </button>

              {/* Expanded View */}
              {isExpanded && (
                <div className="px-6 pb-6 grid grid-cols-4 gap-6 border-t">
                  {/* Left Column: Product Card */}
                  <div className="col-span-1">
                    <div className="border rounded-lg p-4 mt-4">
                      <h4 className="font-semibold text-theme-text-primary mb-3">Product Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-theme-text-secondary">Price:</span>
                          <span className="font-medium">{formatCurrency(product.price || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-theme-text-secondary">Tech Score:</span>
                          <span className="font-medium">{product.techScore}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-theme-text-secondary">Market Appeal:</span>
                          <span className="font-medium">{product.marketAppeal}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-theme-text-secondary">Quality:</span>
                          <span className="font-medium">
                            {product.developmentPhase?.qualityScore != null
                              ? `${product.developmentPhase.qualityScore}%`
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-theme-text-secondary">Rating:</span>
                          <span className="font-medium flex items-center gap-1">
                            {product.salesMetrics?.averageRating ? (
                              <>
                                {product.salesMetrics.averageRating.toFixed(1)}
                                <span className="text-theme-warning text-xs">★</span>
                              </>
                            ) : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-theme-text-secondary">Margin:</span>
                          <span className="font-medium text-theme-success">
                            {product.price && product.productionCost && product.price > 0 
                              ? `${(((product.price - product.productionCost) / product.price) * 100).toFixed(1)}%`
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Columns: Market Segment Data */}
                  <div className="col-span-2 mt-4">
                    <h4 className="font-semibold text-theme-text-primary mb-3">Market Segment Breakdown</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Segment Table */}
                      <div className="border rounded-lg p-3">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 text-theme-text-primary">Segment</th>
                              <th className="text-right py-2 text-theme-text-primary">Units</th>
                              <th className="text-right py-2 text-theme-text-primary">%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(segmentSales).map(([segment, units]) => (
                              <tr key={segment} className="border-b last:border-0">
                                <td className="py-2 text-theme-text-secondary">{segment}</td>
                                <td className="text-right">{units.toLocaleString()}</td>
                                <td className="text-right text-theme-text-secondary">
                                  {((units / (product.salesMetrics?.unitsSold || 1)) * 100).toFixed(1)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pie Chart */}
                      <div className="h-48">
                        {Object.keys(segmentSales).length > 0 && (
                          <Pie data={pieData} options={pieOptions} />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Reviews */}
                  <div className="col-span-1 mt-4">
                    <h4 className="font-semibold text-theme-text-primary mb-3">Product Reviews</h4>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {product.reviews && product.reviews.professional.length > 0 && (
                        <>
                          <div className="text-xs font-medium text-theme-info mb-2">Professional Reviews</div>
                          {product.reviews.professional.map((review, index) => (
                            <div key={`pro-${index}`} className="border border-theme-info/30 bg-theme-info/10 rounded-lg p-3 text-sm">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-theme-info">{review.publication}</span>
                                <div className="flex items-center">
                                  {[...Array(5)].map((_, i) => (
                                    <span key={i} className={`text-xs ${
                                      i < review.rating ? 'text-theme-info' : 'text-theme-text-muted'
                                    }`}>★</span>
                                  ))}
                                </div>
                              </div>
                              <div className="text-xs text-theme-info mb-1">{review.reviewerTitle}</div>
                              <p className="text-theme-text-primary line-clamp-3">{review.text}</p>
                            </div>
                          ))}
                        </>
                      )}
                      
                      {product.reviews && product.reviews.customer.length > 0 && (
                        <>
                          <div className="text-xs font-medium text-theme-text-secondary mb-2 mt-3">Customer Reviews</div>
                          {product.reviews.customer.map((review, index) => (
                            <div key={`cust-${index}`} className="border rounded-lg p-3 text-sm">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-theme-text-primary">{review.author}</span>
                                <div className="flex items-center">
                                  {[...Array(5)].map((_, i) => (
                                    <span key={i} className={`text-xs ${
                                      i < review.rating ? 'text-theme-warning' : 'text-theme-text-muted'
                                    }`}>★</span>
                                  ))}
                                </div>
                              </div>
                              <p className="text-theme-text-secondary line-clamp-3">{review.text}</p>
                            </div>
                          ))}
                        </>
                      )}
                      
                      {(!product.reviews || (product.reviews.professional.length === 0 && product.reviews.customer.length === 0)) && (
                        <div className="text-center py-8 text-theme-text-muted">
                          <p className="text-sm">No reviews available yet</p>
                          <p className="text-xs">Reviews will appear after product release</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Supply & Demand Chart - Full Width */}
                  <div className="col-span-4 mt-4 border-t pt-4">
                    <h4 className="font-semibold text-theme-text-primary mb-3">Supply & Demand Over Time</h4>
                    <SupplyDemandChart
                      data={getProductHistory(
                        product.id,
                        getDisplayDaysForProduct(product.metadata?.releaseDate, currentDate)
                      )}
                      productName={product.name}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
});